/**
 * Shared shapes for the cost ledger and the crew personal ledger.
 * Grok and the offline scorer should write these — not a second ad-hoc row.
 * Crew lines are posted by `crewPersonalLedger`. Flight cost lines are posted by `flightCostLedger`.
 */

export const COST_LINE_CATEGORIES = [
  "fuel",
  "landing",
  "handling",
  "catering",
  "mx_insurance",
  "mx_reserve",
  "other",
] as const;

export type CostLineCategory = (typeof COST_LINE_CATEGORIES)[number];

export const CREW_LEDGER_CATEGORIES = ["pay", "hotel", "meal", "ground", "other"] as const;

export type CrewLedgerCategory = (typeof CREW_LEDGER_CATEGORIES)[number];

export type OpsMoneyCode = "CAD" | "USD" | "EUR" | "GBP";

export type CostLineItem = {
  id: string;
  category: CostLineCategory;
  /** Home-currency amount. Negative is a cost. */
  amount: number;
  currency: OpsMoneyCode;
  missionId?: string;
  flightNumber?: string;
  aircraftId?: string;
  memo?: string;
  createdAtUtc: string;
};

export type CrewLedgerEntry = {
  id: string;
  crewId: string;
  category: CrewLedgerCategory;
  amount: number;
  currency: OpsMoneyCode;
  missionId?: string;
  flightNumber?: string;
  memo?: string;
  createdAtUtc: string;
};

const MONEY: ReadonlySet<string> = new Set(["CAD", "USD", "EUR", "GBP"]);

function text(raw: unknown, max: number): string {
  return String(raw ?? "").trim().slice(0, max);
}

function moneyCode(raw: unknown): OpsMoneyCode {
  const code = text(raw, 3).toUpperCase();
  return MONEY.has(code) ? (code as OpsMoneyCode) : "CAD";
}

function amount(raw: unknown): number | null {
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100) / 100;
}

function isCostCategory(raw: unknown): raw is CostLineCategory {
  return typeof raw === "string" && (COST_LINE_CATEGORIES as readonly string[]).includes(raw);
}

function isCrewCategory(raw: unknown): raw is CrewLedgerCategory {
  return typeof raw === "string" && (CREW_LEDGER_CATEGORIES as readonly string[]).includes(raw);
}

export function hydrateCostLine(raw: unknown): CostLineItem | null {
  if (!raw || typeof raw !== "object") return null;
  const src = raw as Partial<CostLineItem>;
  const id = text(src.id, 64);
  const createdAtUtc = text(src.createdAtUtc, 40);
  const value = amount(src.amount);
  if (!id || !createdAtUtc || value == null || !isCostCategory(src.category)) return null;
  const missionId = text(src.missionId, 64);
  const flightNumber = text(src.flightNumber, 16);
  const aircraftId = text(src.aircraftId, 64);
  const memo = text(src.memo, 120);
  return {
    id,
    category: src.category,
    amount: value,
    currency: moneyCode(src.currency),
    createdAtUtc,
    ...(missionId ? { missionId } : {}),
    ...(flightNumber ? { flightNumber } : {}),
    ...(aircraftId ? { aircraftId } : {}),
    ...(memo ? { memo } : {}),
  };
}

export function hydrateCostLines(raw: unknown): CostLineItem[] {
  if (!Array.isArray(raw)) return [];
  const out: CostLineItem[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    const line = hydrateCostLine(row);
    if (!line || seen.has(line.id)) continue;
    seen.add(line.id);
    out.push(line);
  }
  return out;
}

export function hydrateCrewLedgerEntry(raw: unknown): CrewLedgerEntry | null {
  if (!raw || typeof raw !== "object") return null;
  const src = raw as Partial<CrewLedgerEntry>;
  const id = text(src.id, 64);
  const crewId = text(src.crewId, 64);
  const createdAtUtc = text(src.createdAtUtc, 40);
  const value = amount(src.amount);
  if (!id || !crewId || !createdAtUtc || value == null || !isCrewCategory(src.category)) return null;
  const missionId = text(src.missionId, 64);
  const flightNumber = text(src.flightNumber, 16);
  const memo = text(src.memo, 120);
  return {
    id,
    crewId,
    category: src.category,
    amount: value,
    currency: moneyCode(src.currency),
    createdAtUtc,
    ...(missionId ? { missionId } : {}),
    ...(flightNumber ? { flightNumber } : {}),
    ...(memo ? { memo } : {}),
  };
}

export function hydrateCrewLedger(raw: unknown): CrewLedgerEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: CrewLedgerEntry[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    const entry = hydrateCrewLedgerEntry(row);
    if (!entry || seen.has(entry.id)) continue;
    seen.add(entry.id);
    out.push(entry);
  }
  return out;
}
